import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { CorrectedResponseV2, DialogPromptApi, DialogPromptTypeSpeakV2 } from '../../api/DialogPromptApi';
import ChatBubble from '../ChatBubble';

import AddSelfStudyBlueIcon from '../../asset/icon-add-self-study-blue.png';
import MicrophoneBlueIcon from '../../asset/icon-microphone-blue.png';
import RightArrowBlueIcon from '../../asset/icon-right-arrow-blue.png';
import AudioWaveIcon from '../../asset/icons8-audio-wave.gif';

import { useHistory, useLocation } from 'react-router-dom';
import { useAudioRecorder } from '../../AudioRecorder';
import { hasAnySpokenSentenceScore, sentenceScoreAllGood, sentenceScoreNonSilentFailed, sentenceScoreAnyFailed } from '../../Score';
import { AudioApi } from '../../api/AudioApi';
import { ISentenceScore, ScoreApi } from '../../api/ScoreApi';
import { Sentence, SentenceApi } from '../../api/SentenceApi';
import { UserDialogPromptApi } from '../../api/UserDialogPromptApi';
import AppBarContext from '../../context/AppBarContext';
import { useAudioPlayer } from '../../context/AudioPlayerContext';
import DialogPromptFooterContext from '../../context/DialogPromptFooterContext';
import { scrollElementIntoViewWithFallback } from '../../util/scrollUtils';
import AddSentenceDialog from '../AddSentenceDialog';
import { handleErrorMessage } from '../ErrorDialog';
import SendButtonTextField from '../SendButtonTextField';
import { TeacherAudioActions, getSelectedTeacherSpeakerRole } from '../TeacherAudioReportButton';
import { SentenceScoreMediumResult, SentenceScoreTryAgain, SentenceScoreTryAgainNo, SentenceScoreTryAgainYes, getAudioByText } from './DialogPromptComponent';

interface DialogPromptTypeSpeakV2ComponentProps {
    dialogPrompt: DialogPromptTypeSpeakV2;
    setDialogPromptComplete: (dialogPromptId: number) => void;
    showAvatar?: boolean;
    chatBubbleContainerRef: React.RefObject<HTMLDivElement>;
    isSettings?: boolean;
}


const DialogPromptTypeSpeakV2Component: React.FC<DialogPromptTypeSpeakV2ComponentProps> = ({ dialogPrompt, setDialogPromptComplete, showAvatar = true, chatBubbleContainerRef }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const { playAudio } = useAudioPlayer();

    const { setLeftNavigationList, setRightNavigationList, setActionList, clearFooterList } = React.useContext(DialogPromptFooterContext)

    const [teacherChinesePromptSentence, setTeacherChinesePromptSentence] = React.useState<Sentence | null>(null);

    const [promptAudio, setPromptAudio] = React.useState<Blob | null | undefined>(undefined);
    const [yesResponseAudio, setYesResponseAudio] = React.useState<Blob | null | undefined>(undefined);
    const [teacherChinesePromptAudio, setTeacherChinesePromptAudio] = React.useState<Blob | null | undefined>(undefined);
    const [teacherAskStudentToSpeakPromptAudio, setTeacherAskStudentToSpeakPromptAudio] = React.useState<Blob | null | undefined>(undefined);
    const [closestSentencePromptAudio, setClosestSentencePromptAudio] = React.useState<Blob | null | undefined>(undefined);
    const [spokenYesResponseAudio, setSpokenYesResponseAudio] = React.useState<Blob | null | undefined>(undefined);
    const [spokenNoResponseAudio, setSpokenNoResponseAudio] = React.useState<Blob | null | undefined>(undefined);
    const [sentenceScoreMediumResultAudio, setSentenceScoreMediumResultAudio] = React.useState<Blob | null | undefined>(undefined);
    const [sentenceScoreTryAgainAudio, setSentenceScoreTryAgainAudio] = React.useState<Blob | null | undefined>(undefined);
    const [, setSentenceScoreTryAgainYesAudio] = React.useState<Blob | null | undefined>(undefined);
    const [, setSentenceScoreTryAgainNoAudio] = React.useState<Blob | null | undefined>(undefined);

    React.useEffect(() => {
        clearFooterList()

        getAudioByText(history, setErrorMessage, dialogPrompt.prompt, setPromptAudio)
        getAudioByText(history, setErrorMessage, dialogPrompt.yes_response, setYesResponseAudio)
        getAudioByText(history, setErrorMessage, dialogPrompt.teacher_chinese_prompt, setTeacherChinesePromptAudio)
        getAudioByText(history, setErrorMessage, dialogPrompt.teacher_ask_student_to_speak_prompt, setTeacherAskStudentToSpeakPromptAudio)
        getAudioByText(history, setErrorMessage, dialogPrompt.spoken_yes_response, setSpokenYesResponseAudio)
        getAudioByText(history, setErrorMessage, dialogPrompt.spoken_no_response, setSpokenNoResponseAudio)
        getAudioByText(history, setErrorMessage, SentenceScoreMediumResult, setSentenceScoreMediumResultAudio)
        getAudioByText(history, setErrorMessage, SentenceScoreTryAgain, setSentenceScoreTryAgainAudio)
        getAudioByText(history, setErrorMessage, SentenceScoreTryAgainYes, setSentenceScoreTryAgainYesAudio)
        getAudioByText(history, setErrorMessage, SentenceScoreTryAgainNo, setSentenceScoreTryAgainNoAudio)

        if (dialogPrompt.teacher_chinese_prompt_sentence_id) {
            SentenceApi.getById(dialogPrompt.teacher_chinese_prompt_sentence_id)
                .then((response) => {
                    setTeacherChinesePromptSentence(response.data)
                })
                .catch(err => {
                    handleErrorMessage(err, setErrorMessage, history, location);
                })
        }
    }, [clearFooterList, dialogPrompt.prompt, dialogPrompt.spoken_no_response, dialogPrompt.spoken_yes_response, dialogPrompt.teacher_ask_student_to_speak_prompt, dialogPrompt.teacher_chinese_prompt, dialogPrompt.teacher_chinese_prompt_sentence_id, dialogPrompt.yes_response, history, location, setErrorMessage])

    const [teacherEnglishChatBubbleDone, setTeacherEnglishChatBubbleDone] = React.useState(false);
    const [teacherImageDone, setTeacherImageDone] = React.useState(false);

    const imageRef = React.useRef<HTMLImageElement>(null);

    React.useEffect(() => {
        if (teacherEnglishChatBubbleDone) {
            // console.log("teacherEnglishChatBubbleDone=true");
            if ((dialogPrompt.image_src && dialogPrompt.image_src != "broken") || (dialogPrompt.image_src_url && dialogPrompt.image_src_url != "broken")) {
                const handleImageLoad = () => {
                    setTeacherImageDone(true);
                };

                const imageEl = imageRef.current;
                if (imageEl) {
                    if (imageEl.complete && imageEl.naturalHeight !== 0) {
                        // If image is already loaded
                        handleImageLoad();
                    } else {
                        // Image is not yet loaded, attach event listener
                        imageEl.addEventListener('load', handleImageLoad);
                    }
                }

                return () => {
                    if (imageEl) {
                        imageEl.removeEventListener('load', handleImageLoad);
                    }
                };
            } else {
                setTeacherImageDone(true);
            }
        }
    }, [teacherEnglishChatBubbleDone, dialogPrompt.image_src, dialogPrompt.image_src_url])

    const [teacherChineseChatBubbleDone, setTeacherChineseChatBubbleDone] = React.useState(false);

    const handleTeacherChinesePromptSpeakerClick = React.useCallback(() => {
        if (teacherChinesePromptSentence == null) return;

        const audioPromise = AudioApi.getTeacherSentence(teacherChinesePromptSentence.id)

        audioPromise
            .then(response => {
                // check if the response was successful
                if (response.status >= 300) {
                    throw new Error('Getting male audio was not ok statuscode=' + response.status + " " + response.statusText);
                }
                return response.data;
            })
            .then(blob => {
                playAudio(blob, { onEnded: () => { setTeacherChineseChatBubbleDone(true) } })
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    }, [teacherChinesePromptSentence, playAudio, setErrorMessage, history, location]);

    const teacherChineseAudioReportContext = React.useMemo(() => teacherChinesePromptSentence ? ({
        sentenceId: teacherChinesePromptSentence.id,
        dialogPromptId: dialogPrompt.id,
        textToSpeechSpeakerId: getSelectedTeacherSpeakerRole(),
        audioText: teacherChinesePromptSentence.text,
        audioRole: 'teacher_chinese_prompt',
    }) : null, [dialogPrompt.id, teacherChinesePromptSentence]);

    React.useEffect(() => {
        if (teacherImageDone) {
            // console.log("teacherImageDone=true");
            if (teacherChinesePromptSentence && teacherChinesePromptSentence.text) {
                setActionList([
                    <TeacherAudioActions
                        context={teacherChineseAudioReportContext}
                        onPlay={handleTeacherChinesePromptSpeakerClick}
                        showReportMenu={false}
                    />
                ])
            } else {
                setTeacherChineseChatBubbleDone(true)
            }
        }
    }, [teacherImageDone, teacherChinesePromptSentence, handleTeacherChinesePromptSpeakerClick, setActionList, teacherChineseAudioReportContext])

    function isStudentResponsePromptEmpty(prompt: string | null) {
        return prompt == null || prompt == undefined || prompt.trim().toLowerCase() == "<answer/>"
    }

    const [studentAnswer, setStudentAnswer] = React.useState<string | null>('');
    const [answerSubmitted, setAnswerSubmitted] = React.useState<boolean>(false);
    const [studentChatBubbleDone, setStudentChatBubbleDone] = React.useState(false);
    const [openCancelDialog, setOpenCancelDialog] = React.useState(false);
    const [addSentenceDialogOpen, setAddSentenceDialogOpen] = React.useState(false);

    const handleInputChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        setStudentAnswer(inputValue);
    }, []);

    const handleKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && studentAnswer && studentAnswer.trim().length > 0) {
            setAnswerSubmitted(true);
        }
    }, [studentAnswer]);

    const handleButtonClick = React.useCallback(() => {
        if (studentAnswer && studentAnswer.trim().length > 0) {
            setAnswerSubmitted(true);
        }
    }, [studentAnswer]);

    const handleCancelClick = React.useCallback(() => {
        setOpenCancelDialog(true);
    }, []);

    const handleAddSentenceClick = React.useCallback(() => {
        setAddSentenceDialogOpen(true);
    }, []);

    React.useEffect(() => {
        if (teacherChineseChatBubbleDone) {
            if (answerSubmitted) {
                setLeftNavigationList([
                    <IconButton onClick={handleCancelClick}>
                        <img src={RightArrowBlueIcon} style={{ height: '100%', width: 'auto', maxHeight: '24px' }} />
                    </IconButton>
                ])
            }
            setActionList([
                <SendButtonTextField
                    value={studentAnswer || ''}
                    onChange={handleInputChange}
                    onSubmit={handleButtonClick}
                    onKeyDown={handleKeyDown}
                    required
                    sx={{ width: '80%', flexGrow: 1, mr: 2, mb: 1 }} // Directly control the width here
                    autoFocus
                />
            ])
            if (isStudentResponsePromptEmpty(dialogPrompt.student_response_prompt)) {
                // skip student chat bubble if there is none
                setStudentChatBubbleDone(true);
            }
        }
    }, [studentAnswer, teacherChineseChatBubbleDone, answerSubmitted, dialogPrompt.student_response_prompt, handleButtonClick, handleKeyDown, handleInputChange, handleCancelClick, setActionList, setLeftNavigationList])

    React.useEffect(() => {
        if (studentChatBubbleDone) {
            // console.log("studentChatBubbleDone=true");
            // need to scroll after this effect for some reason.
            // scrolling didn't work after teacherChineseChatBubbleDone
            if (imageRef.current) {
                // console.log("studentChatBubbleDone imageRef scrollIntoView")
                scrollElementIntoViewWithFallback(imageRef.current);
            }
            if (chatBubbleContainerRef.current) {
                scrollElementIntoViewWithFallback(chatBubbleContainerRef.current);
                // console.log("studentChatBubbleDone, chatBubbleContainerRef, scrollIntoView")
            }
        }
    }, [studentChatBubbleDone, chatBubbleContainerRef])

    const [studentAnswerComponentList, setStudentAnswerComponentList] = React.useState<any[]>([]);
    const [studentAnswerDone, setStudentAnswerDone] = React.useState(false);
    const [correctedResponse, setCorrectedResponse] = React.useState<CorrectedResponseV2 | null>(null);
    const canScoreCorrectedResponse = Boolean(
        correctedResponse?.student_response
        && correctedResponse.student_response_sentence_id
        && correctedResponse.student_response_sentence_id > 0
    );

    React.useEffect(() => {
        if (teacherChineseChatBubbleDone && answerSubmitted && studentAnswer) {
            setCorrectedResponse(null);
            setStudentAnswerDone(false);
            setTeacherSpeakRequestDone(false);
            setClosestSentencePromptDone(false);
            setSentenceScoreList([]);
            setChatBubbleSentenceScoreDone([]);
            setChatBubbleTeacherResponseDone([]);
            setTryAgainClickedList([]);
            setChatBubbleTryAgainDone(false);
            setChatBubbleContinueDone(false);
            setClosestSentencePromptAudio(undefined);

            const studentBubble = (
                <ChatBubble
                    isTeacher={false}
                    message={studentAnswer}
                    messageAudio={null} // no audio for students
                    showAvatar={showAvatar}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                    useChineseFont={true}
                />
            );
            setStudentAnswer('');
            setAnswerSubmitted(false);
            setStudentAnswerComponentList((prevComponentList) => [...prevComponentList, studentBubble]);
            DialogPromptApi.getDialogPromptTypeSpeakV2Grade(dialogPrompt.id, studentAnswer)
                .then((response) => {
                    const correctedResponse = response.data;
                    setCorrectedResponse(correctedResponse)
                    if (correctedResponse.is_correct) {
                        const teacherBubble = (
                            <ChatBubble
                                isTeacher={true}
                                message={dialogPrompt.yes_response}
                                messageAudio={yesResponseAudio ? yesResponseAudio : null}
                                showAvatar={showAvatar}
                                chatBubbleContainerRef={chatBubbleContainerRef}
                                setChatBubbleDone={setStudentAnswerDone}
                            />
                        );
                        setStudentAnswerComponentList((prevComponentList) => [...prevComponentList, teacherBubble]);
                        UserDialogPromptApi.addCorrectAnswer(dialogPrompt.id)
                            .catch(err => { handleErrorMessage(err, setErrorMessage, history, location); })
                    } else {
                        const teacherBubble = (
                            <ChatBubble
                                isTeacher={true}
                                message={correctedResponse.reason_for_incorrect}
                                messageAudio={null}  // text is dynamic and not saved
                                showAvatar={showAvatar}
                                chatBubbleContainerRef={chatBubbleContainerRef}
                            />
                        );
                        setStudentAnswerComponentList((prevComponentList) => [...prevComponentList, teacherBubble]);
                        UserDialogPromptApi.addIncorrectAnswer(dialogPrompt.id)
                            .catch(err => { handleErrorMessage(err, setErrorMessage, history, location); })

                    }
                })
                .catch(err => {
                    handleErrorMessage(err, setErrorMessage, history, location);
                })
        }
    }, [answerSubmitted, teacherChineseChatBubbleDone, studentAnswer, dialogPrompt.id, dialogPrompt.yes_response, history, location, setErrorMessage, showAvatar, chatBubbleContainerRef, yesResponseAudio])

    React.useEffect(() => {
        if (canScoreCorrectedResponse && correctedResponse) {
            getAudioByText(history, setErrorMessage, correctedResponse.student_response, setClosestSentencePromptAudio)
        }
    }, [canScoreCorrectedResponse, correctedResponse, history, setErrorMessage])

    React.useEffect(() => {
        if (chatBubbleContainerRef.current) {
            scrollElementIntoViewWithFallback(chatBubbleContainerRef.current);
            // console.log("studentAnswerComponentList, scrollIntoView")
        }
    }, [studentAnswerComponentList, chatBubbleContainerRef])



    const [isScoring, setIsScoring] = React.useState(false);
    const [sentenceScoreList, setSentenceScoreList] = React.useState<ISentenceScore[]>([]);
    const [chatBubbleSentenceScoreDone, setChatBubbleSentenceScoreDone] = React.useState<boolean[]>([]);
    const [chatBubbleTeacherResponseDone, setChatBubbleTeacherResponseDone] = React.useState<boolean[]>([]);
    const [tryAgainClickedList, setTryAgainClickedList] = React.useState<(boolean | null)[]>([]);

    const [teacherSpeakRequestDone, setTeacherSpeakRequestDone] = React.useState(false);
    const [closestSentencePromptDone, setClosestSentencePromptDone] = React.useState(false);

    const [chatBubbleTryAgainDone, setChatBubbleTryAgainDone] = React.useState(false);
    const [chatBubbleContinueDone, setChatBubbleContinueDone] = React.useState(false);

    const [shouldJiggleSkipArrow, setShouldJiggleSkipArrow] = React.useState(false);
    const jiggleKeyframes = React.useMemo(() => ({
        '@keyframes jiggle': {
            '0%': { transform: 'rotate(0deg)' },
            '12.5%': { transform: 'rotate(-20deg)' },
            '37.5%': { transform: 'rotate(20deg)' },
            '52.5%': { transform: 'rotate(-20deg)' },
            '77.5%': { transform: 'rotate(20deg)' },
            '100%': { transform: 'rotate(0)' },
        },
    }), []);
    const jiggleDuration = 500
    const jiggleIteration = 8


    const updateTryAgainClickedList = React.useCallback((tryAgain: boolean) => {
        const index = sentenceScoreList.length - 1;
        setTryAgainClickedList(previous => {
            const updatedArray = [...previous];
            updatedArray[index] = tryAgain;
            return updatedArray;
        })
    }, [sentenceScoreList.length]);

    const handleNextWordClick = React.useCallback(() => {
        setChatBubbleTryAgainDone(false)
        updateTryAgainClickedList(false)
        setChatBubbleContinueDone(true)
    }, [updateTryAgainClickedList]);

    const handleTryAgainClick = React.useCallback(() => {
        setChatBubbleTryAgainDone(false)
        updateTryAgainClickedList(true)
    }, [updateTryAgainClickedList]);

    const { startRecording, stopRecording, isRecording } = useAudioRecorder({
        setErrorMessage,
        onStop: (audioData) => {
            if (!canScoreCorrectedResponse || !correctedResponse) {
                setIsScoring(false);
                return;
            }

            setIsScoring(true)
            const formData = new FormData();
            formData.append('audio', audioData);

            ScoreApi.getScore(correctedResponse.student_response_sentence_id, formData)
                .then(response => {
                    setIsScoring(false)
                    // check if the response was successful
                    if (response.status >= 300) {
                        throw new Error('Scoring audio was not ok statuscode=' + response.status + " " + response.statusText);
                    }
                    return response.data;
                })
                .then(responseJson => {
                    UserDialogPromptApi.addSpeakingAttempt(dialogPrompt.id, responseJson.score_mean)
                        .catch(err => { handleErrorMessage(err, setErrorMessage, history, location); })
                    responseJson.audioData = audioData;
                    setSentenceScoreList((previous) => [...previous, responseJson]);
                })
                .catch(err => {
                    setIsScoring(false);
                    handleErrorMessage(err, setErrorMessage, history, location);
                })
        }
    });

    React.useEffect(() => {
        if (sentenceScoreList.length === 0) return;

        const last = sentenceScoreList[sentenceScoreList.length - 1];
        if (!sentenceScoreNonSilentFailed(last)) {
            setShouldJiggleSkipArrow(false);
            return;
        }

        const failedCount = sentenceScoreList.reduce(
            (acc, sentenceScore) => acc + (sentenceScoreNonSilentFailed(sentenceScore) ? 1 : 0),
            0
        );

        if (failedCount % 2 === 0) {
            setShouldJiggleSkipArrow(true);
            const totalDuration = jiggleDuration * jiggleIteration
            const t = setTimeout(() => setShouldJiggleSkipArrow(false), totalDuration + 50); // length of the animation
            return () => clearTimeout(t);
        } else {
            setShouldJiggleSkipArrow(false);
        }
    }, [sentenceScoreList]);

    React.useEffect(() => {
        if (studentAnswerDone && canScoreCorrectedResponse && chatBubbleTryAgainDone == false) {
            if (sentenceScoreList.length == 0 || sentenceScoreAnyFailed(sentenceScoreList[sentenceScoreList.length - 1]) || tryAgainClickedList[sentenceScoreList.length - 1]) {
                if (hasAnySpokenSentenceScore(sentenceScoreList)) {
                    setLeftNavigationList([
                        <IconButton
                            onClick={handleCancelClick}
                            sx={{
                                ...jiggleKeyframes,
                                animation: shouldJiggleSkipArrow ? `jiggle ${jiggleDuration}ms ease-in-out ${jiggleIteration}` : 'none',
                            }}
                        >
                            <img src={RightArrowBlueIcon} style={{ height: '100%', width: 'auto', maxHeight: '24px' }} />
                        </IconButton>
                    ])
                } else {
                    setLeftNavigationList([])
                }
                setRightNavigationList([
                    <Tooltip title="Add to Pronunciation" arrow>
                        <IconButton onClick={handleAddSentenceClick} sx={{ alignSelf: 'center' }}>
                            <img src={AddSelfStudyBlueIcon} style={{ height: '100%', width: 'auto', maxHeight: '24px' }} />
                        </IconButton>
                    </Tooltip>
                ])
                setActionList([
                    <IconButton onClick={isRecording ? stopRecording : startRecording}>
                        {isRecording ? <img src={AudioWaveIcon} alt="Recording" style={{ height: '100%', width: 'auto', maxHeight: '36px' }} />
                            : isScoring ? <CircularProgress size={24} sx={{ height: '100%', width: 'auto', maxHeight: '36px' }} />
                                : <img src={MicrophoneBlueIcon} style={{ height: '100%', width: 'auto', maxHeight: '36px' }} />}
                    </IconButton>
                ])
            }
        }
    }, [
        sentenceScoreList, isRecording, isScoring, teacherSpeakRequestDone, canScoreCorrectedResponse,
        chatBubbleTryAgainDone, shouldJiggleSkipArrow, jiggleKeyframes, setActionList, setLeftNavigationList,
        setRightNavigationList, startRecording, stopRecording, studentAnswerDone, tryAgainClickedList,
        handleCancelClick, handleAddSentenceClick,
    ]);

    React.useEffect(() => {
        if (chatBubbleContainerRef.current) {
            scrollElementIntoViewWithFallback(chatBubbleContainerRef.current);
            // console.log("sentenceScoreList, scrollIntoView")
        }
    }, [sentenceScoreList, chatBubbleContainerRef]);


    React.useEffect(() => {
        if (chatBubbleTryAgainDone) {
            setLeftNavigationList([
                <IconButton onClick={handleCancelClick}>
                    <img src={RightArrowBlueIcon} style={{ height: '100%', width: 'auto', maxHeight: '24px' }} />
                </IconButton>
            ])
            setActionList([
                <Button variant="contained" onClick={handleNextWordClick} className='secondaryButton'>
                    No, move on
                </Button>,
                <Button variant="contained" onClick={handleTryAgainClick} >
                    Yes, try again
                </Button>
            ])
        }
    }, [chatBubbleTryAgainDone, handleNextWordClick, handleTryAgainClick, setActionList, setLeftNavigationList, handleCancelClick]);

    const handleCancelDialogConfirm = () => {
        setOpenCancelDialog(false);
        if (studentAnswerDone || teacherSpeakRequestDone || closestSentencePromptDone) {
            // we're past the typing, so we're in the speaking. skip the remainder of the exercise
            setChatBubbleContinueDone(true);
        } else if (correctedResponse) {
            // didn't pass typing, but did they try at all?
            // then only skip to typing part
            setTeacherChineseChatBubbleDone(true);
            setAnswerSubmitted(true);
            setStudentAnswerDone(true);
        } else {
            // skip to end? TODO, create an answer for them to speak
            setChatBubbleContinueDone(true);
            // // we're somewhere in the beginning? skip only the typing part
            // const response = dialogPrompt.dialog_prompt_sentence_list[0].student_response;
            // if (response === undefined || response === null) {
            //     throw Error("Empty dialogPrompt.dialog_prompt_sentence_list[0].student_response");
            // }
            // setCorrectedResponse(new CorrectedResponse(false, "Please speak this sentence.", response, response, dialogPrompt.dialog_prompt_sentence_list[0].id))
            // setStudentAnswerDone(true);
        }
    };

    const handleCancelDialogCancel = () => {
        setOpenCancelDialog(false);
    };


    React.useEffect(() => {
        if (chatBubbleContinueDone) {
            clearFooterList()
            setDialogPromptComplete(dialogPrompt.id)

        }
    }, [chatBubbleContinueDone, clearFooterList, dialogPrompt.id, setDialogPromptComplete])

    return (
        <Stack>
            <ChatBubble
                isTeacher={dialogPrompt.is_teacher}
                message={dialogPrompt.prompt}
                messageAudio={promptAudio ? promptAudio : null}
                showAvatar={showAvatar}
                chatBubbleContainerRef={chatBubbleContainerRef}
                setChatBubbleDone={setTeacherEnglishChatBubbleDone}
            />
            {(teacherEnglishChatBubbleDone && (dialogPrompt.image_src || dialogPrompt.image_src_url)) && (<>
                <img
                    ref={imageRef}
                    src={dialogPrompt.image_src ? dialogPrompt.image_src : (dialogPrompt.image_src_url ? dialogPrompt.image_src_url : undefined)}
                    alt={dialogPrompt.teacher_chinese_prompt ? dialogPrompt.teacher_chinese_prompt : ""}
                    style={{
                        maxHeight: '200px', maxWidth: '200px',
                        height: 'auto', width: 'auto',
                        margin: '0 auto', // Add this line to center the image horizontally
                        display: 'block', // Add this line to ensure the margin works correctly
                    }}
                />
            </>)}
            {(teacherImageDone && teacherChinesePromptSentence && teacherChinesePromptSentence.text) && (<>
                <ChatBubble
                    isTeacher={true}
                    message={teacherChinesePromptSentence && teacherChinesePromptSentence.text ? teacherChinesePromptSentence.text : ''}
                    messageAudio={teacherChinesePromptAudio ? teacherChinesePromptAudio : null}
                    showAvatar={showAvatar}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                    setChatBubbleDone={setTeacherChineseChatBubbleDone}
                    useChineseFont={true}
                />
            </>)}
            {(teacherChineseChatBubbleDone && !isStudentResponsePromptEmpty(dialogPrompt.student_response_prompt)) && (
                <ChatBubble
                    isTeacher={false}
                    message={dialogPrompt.student_response_prompt ? dialogPrompt.student_response_prompt : ""}
                    messageAudio={null} // no audio for student
                    showAvatar={showAvatar}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                    setChatBubbleDone={setStudentChatBubbleDone}
                    useChineseFont={true}
                />
            )}

            {studentAnswerComponentList.map((component, index) => (
                <Box key={index} component="div">
                    {component}
                </Box>
            ))}

            {canScoreCorrectedResponse && correctedResponse && studentAnswerDone && (
                <ChatBubble
                    isTeacher={true}
                    message={dialogPrompt.teacher_ask_student_to_speak_prompt ? dialogPrompt.teacher_ask_student_to_speak_prompt : ""}
                    messageAudio={teacherAskStudentToSpeakPromptAudio ? teacherAskStudentToSpeakPromptAudio : null}
                    showAvatar={showAvatar}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                    setChatBubbleDone={setTeacherSpeakRequestDone}
                />
            )}
            {canScoreCorrectedResponse && correctedResponse && teacherSpeakRequestDone && (
                <ChatBubble
                    isTeacher={true}
                    message={correctedResponse ? correctedResponse.student_response : ""}
                    messageAudio={closestSentencePromptAudio ? closestSentencePromptAudio : null}
                    showAvatar={showAvatar}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                    setChatBubbleDone={setClosestSentencePromptDone}
                    listenSentenceId={correctedResponse.student_response_sentence_id}
                />
            )}

            {sentenceScoreList.map((sentenceScore, index) => (
                <div key={index}>
                    <ChatBubble
                        isTeacher={false}
                        message={sentenceScore.text}
                        messageAudio={null} // audio will be handled through sentence score
                        sentenceScore={sentenceScore}
                        showAvatar={showAvatar}
                        chatBubbleContainerRef={chatBubbleContainerRef}
                        setChatBubbleDone={(done) => setChatBubbleSentenceScoreDone(previous => {
                            // console.log("sentene score updated index " + index)
                            const updatedArray = [...previous];
                            updatedArray[index] = done;
                            return updatedArray;
                        })}
                    />
                    {chatBubbleSentenceScoreDone[index] && (<>
                        {sentenceScoreAllGood(sentenceScore) ? (
                            <ChatBubble
                                isTeacher={true}
                                message={dialogPrompt.spoken_yes_response ? dialogPrompt.spoken_yes_response : ""}
                                messageAudio={spokenYesResponseAudio ? spokenYesResponseAudio : null}
                                showAvatar={showAvatar}
                                chatBubbleContainerRef={chatBubbleContainerRef}
                                setChatBubbleDone={setChatBubbleContinueDone}
                            />
                        ) : sentenceScoreAnyFailed(sentenceScore) ? (
                            <ChatBubble isTeacher={dialogPrompt.is_teacher}
                                message={dialogPrompt.spoken_no_response ? dialogPrompt.spoken_no_response : ""}
                                messageAudio={spokenNoResponseAudio ? spokenNoResponseAudio : null}
                                showAvatar={showAvatar}
                                chatBubbleContainerRef={chatBubbleContainerRef}
                                setChatBubbleDone={() => {
                                    setChatBubbleTryAgainDone(false);
                                    // set null, to skip this index
                                    setTryAgainClickedList((previous) => [...previous, null]);
                                }}
                            />
                        ) : (
                            <ChatBubble isTeacher={dialogPrompt.is_teacher}
                                message={SentenceScoreMediumResult}
                                messageAudio={sentenceScoreMediumResultAudio ? sentenceScoreMediumResultAudio : null}
                                showAvatar={showAvatar}
                                chatBubbleContainerRef={chatBubbleContainerRef}
                                setChatBubbleDone={(done) => setChatBubbleTeacherResponseDone(previous => {
                                    const updatedArray = [...previous];
                                    updatedArray[index] = done;
                                    return updatedArray;
                                })}
                            />
                        )}
                        {chatBubbleTeacherResponseDone[index] && (
                            <ChatBubble
                                isTeacher={true}
                                message={SentenceScoreTryAgain}
                                messageAudio={sentenceScoreTryAgainAudio ? sentenceScoreTryAgainAudio : null}
                                showAvatar={showAvatar}
                                chatBubbleContainerRef={chatBubbleContainerRef}
                                setChatBubbleDone={setChatBubbleTryAgainDone}
                            />
                        )}
                        {tryAgainClickedList[index] === true && (
                            <ChatBubble
                                isTeacher={false}
                                message={SentenceScoreTryAgainYes}
                                messageAudio={null} // no audio for student
                                showAvatar={showAvatar}
                                chatBubbleContainerRef={chatBubbleContainerRef}
                            />
                        )}
                        {tryAgainClickedList[index] === false && (
                            <ChatBubble
                                isTeacher={false}
                                message={SentenceScoreTryAgainNo}
                                messageAudio={null} // no audio for student
                                showAvatar={showAvatar}
                                chatBubbleContainerRef={chatBubbleContainerRef}
                                setChatBubbleDone={setChatBubbleContinueDone}
                            />

                        )}
                    </>)}
                </div>
            ))}
            {/* Confirmation Dialog */}
            <Dialog open={openCancelDialog} onClose={handleCancelDialogCancel}>
                <DialogTitle><Typography variant="h4" sx={{ color: '#383838' }}>Confirm Skip</Typography></DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#8D8D8D' }}>
                        Are you sure you want to skip this exercise?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between', '& .MuiButton-root': { flex: 1 } }}>
                    <Button onClick={handleCancelDialogCancel} color="primary" sx={{ color: '#787878' }}>
                        Keep trying
                    </Button>
                    <Button onClick={handleCancelDialogConfirm} color="primary" sx={{ color: '#787878' }}>
                        Skip
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Add Sentence Confirmation Dialog */}
            <AddSentenceDialog
            open={addSentenceDialogOpen}
            sentence={
                canScoreCorrectedResponse && correctedResponse
                ? { text: correctedResponse.student_response, id: correctedResponse.student_response_sentence_id }
                : null
            }
            onClose={() => setAddSentenceDialogOpen(false)}
            />
        </Stack >
    );
};

export default DialogPromptTypeSpeakV2Component;
